<script setup lang="ts">
import type { ObjectId } from '~/store/radios'
import type { components } from '~/generated/types'

import { useI18n } from 'vue-i18n'
import { useStore } from '~/store'
import { computed } from 'vue'

import Button from '~/ui/Button.vue'

interface Props {
  customRadioId?: number | null
  type?: string
  clientOnly?: boolean
  objectId?: ObjectId | number | string | null
  radioConfig?: components['schemas']['RadioConfig'][] | null
  playOnly?: boolean
  lowHeight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  customRadioId: null,
  type: '',
  clientOnly: false,
  objectId: null,
  radioConfig: null,
  playOnly: false,
  lowHeight: false
})

const store = useStore()
const effectiveType = computed(() => props.customRadioId ? 'custom' : props.type)

const running = computed(() => {
  if (!store.state.radios.running) {
    return false
  }

  const { current } = store.state.radios
  return current?.type === effectiveType.value
    && current?.customRadioId === props.customRadioId
    && (
      (typeof props.objectId === 'object' && current.objectId?.fullUsername === props.objectId?.fullUsername)
        || current.objectId === props.objectId
    )
})

const { t } = useI18n()

// The button only need the first config. If the Button comes from an CustomRadio and the type is troi
// we don't want to trigger the custom radio mechanism in the back ?
const firstRadioConfig = computed(() => props.radioConfig?.[0])

const buttonLabel = computed(() => {
  switch (props.type) {
    case 'tag':
      return running.value
        ? t('components.radios.Button.stopTagsRadio')
        : t('components.radios.Button.startTagsRadio')
    case 'artist':
      return running.value
        ? t('components.radios.Button.stopArtistsRadio')
        : t('components.radios.Button.startArtistsRadio')
    case 'troi':
      if ( firstRadioConfig?.value?.type === 'lb-radio' && firstRadioConfig?.value?.prompt?.startsWith('artist')) {
        return running.value
          ? t('components.radios.Button.stopArtistTroiRadio')
          : t('components.radios.Button.startArtistTroiRadio')
      } else {
        return running.value
          ? t('components.radios.Button.stopTroiRadio')
          : t('components.radios.Button.startTroiRadio')
      }
    case 'playlist':
      return running.value
        ? t('components.radios.Button.stopPlaylistsRadio')
        : t('components.radios.Button.startPlaylistsRadio')
    case 'actor-content':
      return running.value
        ? t('components.radios.Button.stopActorRadio')
        : t('components.radios.Button.startActorRadio')
    case 'actor-activity':
      return running.value
        ? t('components.radios.Button.stopActorActivityRadio')
        : t('components.radios.Button.startActorActivityRadio')
    default:
      return running.value
        ? t('components.radios.Button.stopRadio')
        : t('components.radios.Button.startRadio')
  }
})
const tooltipLabel = computed(() => {
  switch (props.type) {
    case 'tag':
      return t('components.radios.Button.tooltipTagsRadio')
    case 'artist':
            return t('components.radios.Button.tooltipArtistsRadio')
    case 'playlist':
          return t('components.radios.Button.tooltipPlaylistsRadio')
    case 'actor-activity':
           return t('components.radios.Button.tooltipActorActivityRadio')
    case 'actor-content':
          return t('components.radios.Button.tooltipActorRadio')
    case 'troi':
          if ( firstRadioConfig?.value?.type === 'lb-radio' && firstRadioConfig?.value?.prompt?.startsWith('artist')) {
                return t('components.radios.Button.tooltipArtistTroiRadio')
              } else {
                return t('components.radios.Button.tooltipTroiRadio')}
    default:
            return t('components.radios.Button.tooltipRadio')
  }
})


const toggleRadio = () => {
  if (running.value) {
    return store.dispatch('radios/stop')
  }

  return store.dispatch('radios/start', {
    type: effectiveType.value,
    objectId: props.objectId,
    customRadioId: props.customRadioId,
    clientOnly: props.clientOnly,
    config: firstRadioConfig.value,
    playOnly: props.playOnly
  })
}
</script>

<template>
  <tooltip :content="tooltipLabel">
    <Button
      :aria-pressed="running"
      :class="`color-primary-solid shape-${store.state.auth.authenticated && playOnly ? 'circle' : 'field-minimal'}`"
      @click="toggleRadio"
      :ui-block-size="lowHeight ? '40' : undefined"
    >
      <Icon
        :icon="running ? 'bi:pause-fill' : 'bi:play-fill'"
        style="font-size: 2em;"
      />
      <div v-if="!playOnly">
        {{ buttonLabel }}
      </div>
    </Button>
  </tooltip>
</template>
